'use client';

/**
 * GUESS THE GOAL — LIVE. Server-authoritative version of the tactics-board
 * quiz: sessions, options, scoring and rewards all come from the backend; this
 * screen never knows the answer until the server reveals it. The replay clock
 * is derived from the SERVER clock (server_now offset), so the displayed
 * potential always matches what the backend will actually score — including
 * after a resume, and it keeps decaying while the tab is hidden.
 *
 * Deliberately does NOT import the demo component or its bundled goal data:
 * the demo's client-side answers must never enter this route's module graph.
 *
 * Framed as a NATIVE app page (transparent background, hub-style header) —
 * not the MiniGameShell, whose painted backdrop made the game read as a
 * separate window inside the app shell.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import Link from 'next/link';
import { ArrowLeft, ChevronRight, Clapperboard, LayoutGrid, Play, RotateCcw, Star, WifiOff, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { TacticsBoard2D, BOARD_VIEW_W, BOARD_VIEW_H } from './TacticsBoard2D';
import { GgtActionGlyph, GgtLegend, GGT_ACTION_META, GGT_OPTION_CLASS, ggtOptionStyle, type GgtOptionState } from './guessTheGoalUi';
import { buildTimeline, type TacticsGoalDef, type TacticsStepKind } from '../lib/tacticsEngine';
import { type MiniLocale, useMiniLocale, useMiniT } from '../lib/i18n';
import { useLocale } from '@/contexts/LocaleContext';
import { queryKeys } from '@/lib/queries/queryKeys';
import { trackEvent } from '@/lib/posthog';
import { usePlayer } from '@/contexts/PlayerContext';
import { useAuthStore } from '@/stores/auth.store';
import { CoinIcon } from '@/features/store/components/CoinIcon';
import {
  guessTheGoalApi,
  type GgtGuessOutcome,
  type GgtBonusOutcome,
  type GgtGallery,
  type GgtI18nText,
  type GgtSession,
} from '@/lib/repositories/guessTheGoal.repo';

/** What a partner play adds to the shared payloads (backend partners/games/guess-the-goal). */
export interface GgtPlayEnd {
  play_id: string;
  score: number;
  sent: boolean;
}
export type GgtLiveSession = GgtSession & { bonus_deadline?: string | null };
export type GgtLiveGuessOutcome = GgtGuessOutcome & {
  timed_out?: boolean;
  bonus_deadline?: string | null;
  finished?: GgtPlayEnd | null;
};
export type GgtLiveBonusOutcome = GgtBonusOutcome & {
  timed_out?: boolean;
  finished?: GgtPlayEnd | null;
  /** Partner plays send the footage and fun fact only once the bonus is closed (they can give its answer away). */
  fun_fact?: GgtGuessOutcome['fun_fact'];
  video_url?: string | null;
  clip_start_s?: number | null;
  clip_end_s?: number | null;
};

function withFootage(outcome: GgtLiveGuessOutcome | null, bonus: GgtLiveBonusOutcome): GgtLiveGuessOutcome | null {
  if (!outcome || bonus.video_url === undefined) return outcome;
  return {
    ...outcome,
    fun_fact: bonus.fun_fact ?? null,
    video_url: bonus.video_url,
    clip_start_s: bonus.clip_start_s ?? null,
    clip_end_s: bonus.clip_end_s ?? null,
  };
}

export interface GgtPlayView {
  session: GgtLiveSession | null;
  finished: GgtPlayEnd | null;
  outcome: GgtLiveGuessOutcome | null;
  bonus: GgtLiveBonusOutcome | null;
}

export interface GgtScreenApi {
  current(): Promise<GgtLiveSession | null>;
  start(clientNonce: string): Promise<GgtLiveSession>;
  guess(sessionId: string, optionId: string): Promise<GgtLiveGuessOutcome>;
  bonus(sessionId: string, optionId: string): Promise<GgtLiveBonusOutcome>;
  gallery?(): Promise<GgtGallery>;
  devResetToday?(): Promise<unknown>;
  devAllGoals?(): ReturnType<typeof guessTheGoalApi.devAllGoals>;
}

/** Freecroco mode: points instead of coins, deadlines, one play, and the host's result screen at the end. */
export interface GgtPartnerMode {
  /** Settles a play whose bonus time ran out; resolves to the timed-out outcome. */
  expire(sessionId: string): Promise<{ finished: GgtPlayEnd; outcome: GgtLiveGuessOutcome; bonus: GgtLiveBonusOutcome | null }>;
  /** The play behind a session, finished ones included: recovers a lost or refused response. */
  lookup(sessionId: string): Promise<GgtPlayView>;
  onFinished(play: GgtPlayEnd): void;
  onExit(): void;
  copy: {
    subtitle: string;
    intro: string;
    seeResult: string;
    timeLeft: (seconds: number) => string;
    timeUp: string;
    noPlaysLeft: string;
  };
}

/** A signed-out visitor on a public game page: the guest session plays, earns nothing, and is invited to sign in. */
export interface GgtGuestMode {
  onExit(): void;
  /** Engine lifecycle for the public page's analytics: a goal started, a goal finished. */
  onEvent?(event: 'start' | 'complete' | 'replay'): void;
  signInHref: string;
}

export interface GgtScreenDeps {
  api: GgtScreenApi;
  /** Coins / XP the server granted (quizball.io only). */
  onRewards?: (awards: GgtGuessOutcome['awards']) => void;
  track?: (event: string, properties?: Record<string, unknown>) => void;
  isAdmin?: boolean;
  partner?: GgtPartnerMode;
  guest?: GgtGuestMode;
}

const noTrack = () => {};

/** Status and machine code of either client's errors (quizball.io GuessTheGoalApiError, partner PartnerApiError). */
function apiError(err: unknown): { status: number; code: string | null } | null {
  const e = err as { status?: unknown; code?: unknown } | null;
  return e && typeof e.status === 'number' ? { status: e.status, code: typeof e.code === 'string' ? e.code : null } : null;
}

function secondsUntil(deadline: string | null | undefined, offsetMs: number): number | null {
  if (!deadline) return null;
  return Math.max(0, Math.ceil((new Date(deadline).getTime() - (Date.now() + offsetMs)) / 1000));
}

type Phase =
  | 'loading'
  | 'load_error'
  | 'idle'
  | 'starting'
  | 'watch'
  | 'reveal'
  | 'bonus'
  | 'bonus_done'
  | 'disabled';

const LOOP_HOLD = 1.6;
/** Display-clock pad: the server_now offset lags by response transit, and the
 *  guess itself takes a request to arrive — bias the shown clock FORWARD so
 *  the displayed potential can only understate what the server will score. */
const SKEW_PAD_MS = 400;

/** watch?v=, youtu.be/, shorts/, embed/ → bare video id (null if unparseable). */
const DIFF_META: Record<string, { label: Record<MiniLocale, string>; color: string }> = {
  easy: { label: { en: 'Easy', ka: 'მარტივი', es: 'Fácil', tr: 'Kolay', ru: 'Легко', pt: 'Fácil', de: 'Leicht' }, color: '#38B60E' },
  medium: { label: { en: 'Medium', ka: 'საშუალო', es: 'Medio', tr: 'Orta', ru: 'Средне', pt: 'Médio', de: 'Mittel' }, color: '#FFE500' },
  hard: { label: { en: 'Hard', ka: 'რთული', es: 'Difícil', tr: 'Zor', ru: 'Сложно', pt: 'Difícil', de: 'Schwer' }, color: '#FB3101' },
};

/** Looping board replay for the collection overlay — its own clock, nothing
 *  shared with the live session. */
function GgtBoardReplay({
  players,
  steps,
}: {
  players: NonNullable<GgtGallery['goals'][number]['players']>;
  steps: NonNullable<GgtGallery['goals'][number]['steps']>;
}) {
  const goal = useMemo<TacticsGoalDef>(
    () => ({
      id: 'gallery-replay',
      title: '',
      options: [],
      answerIndex: -1,
      funFact: '',
      players: players as TacticsGoalDef['players'],
      steps: steps as TacticsGoalDef['steps'],
      bonus: { question: '', options: [], answerIndex: -1 },
    }),
    [players, steps]
  );
  const timeline = useMemo(() => buildTimeline(goal), [goal]);
  const [t, setT] = useState(0);
  useEffect(() => {
    const startedAt = performance.now();
    let raf = 0;
    const tick = () => {
      setT(((performance.now() - startedAt) / 1000) % (timeline.duration + LOOP_HOLD));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [timeline]);
  const anim = Math.min(t, timeline.duration);
  return (
    <div
      className="w-full overflow-hidden rounded-2xl bg-black"
      style={{ aspectRatio: `${BOARD_VIEW_W} / ${BOARD_VIEW_H}` }}
    >
      <TacticsBoard2D
        goal={goal}
        timeline={timeline}
        t={anim}
        goalFlash={timeline.duration > 0 && anim >= timeline.duration - 0.05}
      />
    </div>
  );
}

function GgtGalleryPanel({
  gallery,
  locale,
  t,
  pick,
  onClose,
  earnings = true,
}: {
  gallery: GgtGallery;
  /** Guests earn nothing, so their collection shows no coin/XP totals. */
  earnings?: boolean;
  locale: MiniLocale;
  t: (key: string, vars?: Record<string, string | number>) => string;
  pick: (text: GgtI18nText | null | undefined) => string;
  onClose?: () => void;
}) {
  const [watch, setWatch] = useState<{
    goal: GgtGallery['goals'][number];
    title: string;
    mode: 'board' | 'video';
  } | null>(null);
  const pct = gallery.total > 0 ? Math.round((gallery.solved / gallery.total) * 100) : 0;
  const rank: Record<string, number> = { easy: 0, medium: 1, hard: 2 };
  // Solved cards then locked placeholders, grouped easy -> medium -> hard.
  // Unsolved goals arrive as counts only — the server never enumerates them.
  const difficulties = [
    ...new Set([...gallery.goals.map((g) => g.difficulty), ...Object.keys(gallery.locked)]),
  ].sort((a, b) => (rank[a] ?? 3) - (rank[b] ?? 3));
  const ordered = difficulties.flatMap((difficulty) => [
    ...gallery.goals
      .filter((g) => g.difficulty === difficulty)
      .map((goal) => ({ kind: 'solved' as const, difficulty, goal })),
    ...Array.from({ length: gallery.locked[difficulty] ?? 0 }, () => ({
      kind: 'locked' as const,
      difficulty,
      goal: null,
    })),
  ]);
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between gap-2 px-1">
        <p className="font-poppins text-[12px] font-black uppercase tracking-wider text-white/60">
          {t('Your collection')}
        </p>
        <div className="flex items-center gap-3">
          <p className="font-poppins text-[12px] font-black uppercase tracking-wider text-white">
            {gallery.solved}
            <span className="text-white/40">/{gallery.total}</span>
          </p>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('Back')}
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-blue text-white transition-all hover:brightness-110 active:translate-y-[1px]"
            >
              <X className="size-5" />
            </button>
          )}
        </div>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-brand-green-bright transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {earnings && (
        <div className="flex items-center gap-4 px-1">
          <span className="flex items-center gap-1.5 font-poppins text-[12px] font-black uppercase text-brand-green-bright">
            <CoinIcon className="size-4" /> {gallery.coins_earned}
          </span>
          <span className="font-poppins text-[12px] font-black uppercase text-white/70">
            {gallery.xp_earned} XP
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-4">
        {ordered.map((entry, i) =>
          entry.kind === 'solved' ? (
            <button
              key={`s-${i}`}
              type="button"
              disabled={!entry.goal.video_url && !(entry.goal.players && entry.goal.steps)}
              onClick={() =>
                (entry.goal.video_url || (entry.goal.players && entry.goal.steps)) &&
                setWatch({
                  goal: entry.goal,
                  title: pick(entry.goal.title),
                  mode: entry.goal.players && entry.goal.steps ? 'board' : 'video',
                })
              }
              className="flex min-h-[92px] flex-col items-center justify-between gap-2 rounded-xl bg-brand-green-bright p-3 text-center transition-all enabled:cursor-pointer enabled:hover:brightness-105 enabled:active:translate-y-[1px]"
            >
              <p className="font-poppins text-[11px] font-black uppercase leading-snug text-black">
                {pick(entry.goal.title)}
              </p>
              <div className="flex w-full items-center justify-between gap-1">
                <span className="font-poppins text-[10px] font-black uppercase text-black">
                  +{entry.goal.points}
                </span>
                <span className="flex items-center gap-1.5">
                  {entry.goal.bonus_correct && <Star className="size-3 fill-black text-black" />}
                  {entry.goal.video_url && <Clapperboard className="size-3 text-black/60" />}
                  <span className="font-poppins text-[10px] font-black text-black/60">
                    {entry.goal.year}
                  </span>
                </span>
              </div>
            </button>
          ) : (
            <div
              key={`u-${i}`}
              className="flex min-h-[92px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-white/15 p-3"
            >
              <span className="font-poppins text-lg font-black text-white/25">???</span>
              <span
                className="rounded-full px-2 py-0.5 font-poppins text-[9px] font-black uppercase tracking-wide"
                style={{
                  color: DIFF_META[entry.difficulty]?.color ?? '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                }}
              >
                {DIFF_META[entry.difficulty]?.label[locale] ?? entry.difficulty}
              </span>
            </div>
          )
        )}
      </div>
      {watch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setWatch(null)}>
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="mb-2 text-center font-poppins text-[12px] font-black uppercase text-white">{watch.title}</p>
            {watch.goal.players && watch.goal.steps && watch.goal.video_url && (
              <div className="mb-2 flex items-center justify-center gap-2">
                {(['board', 'video'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setWatch({ ...watch, mode })}
                    className={`rounded-full px-4 py-1.5 font-poppins text-[11px] font-black uppercase tracking-wide transition-all ${
                      mode === 'board' ? 'bg-brand-yellow text-black' : 'bg-brand-blue text-white'
                    } ${watch.mode === mode ? 'ring-2 ring-white' : 'opacity-55 hover:opacity-90'}`}
                  >
                    {mode === 'board' ? t('Board') : t('Video')}
                  </button>
                ))}
              </div>
            )}
            {watch.mode === 'board' && watch.goal.players && watch.goal.steps ? (
              <GgtBoardReplay players={watch.goal.players} steps={watch.goal.steps} />
            ) : !watch.goal.video_url ? null : /^https?:\/\/[^ ]+\.mp4(\?|$)/i.test(watch.goal.video_url) ? (
              <video src={watch.goal.video_url} autoPlay controls playsInline className="aspect-video w-full rounded-2xl bg-black" />
            ) : (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${(watch.goal.video_url.match(/(?:watch\?v=|youtu\.be\/|shorts\/|embed\/)([A-Za-z0-9_-]{6,20})/) ?? [])[1] ?? ''}?autoplay=1&rel=0${watch.goal.clip_start_s ? `&start=${watch.goal.clip_start_s}` : ''}${watch.goal.clip_end_s ? `&end=${watch.goal.clip_end_s}` : ''}`}
                title={watch.title}
                allow="autoplay; encrypted-media"
                // The partner host sends no referrer by default; YouTube embeds refuse to play without one.
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="aspect-video w-full rounded-2xl bg-black"
              />
            )}
            <button type="button" onClick={() => setWatch(null)} className="mx-auto mt-3 block rounded-full bg-white/15 px-5 py-2 font-poppins text-[12px] font-black uppercase text-white">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function youtubeEmbed(url: string, clipEndS?: number | null): { id: string; start: number; end?: number } | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/
  );
  if (!m) return null;
  const t = url.match(/[?&](?:t|start)=(\d+)/);
  const start = t ? Number(t[1]) : 0;
  // A verified clip window beats the URL's own offset — the player stops at
  // the end of the goal sequence instead of rolling into post-match footage.
  const end = clipEndS && clipEndS > start ? clipEndS : undefined;
  return { id: m[1], start, end };
}

function newNonce(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

/** Adapt a served session into the shape the board renderer expects. The
 *  fields the demo data carries but the server withholds (title, correctness)
 *  are filled with inert placeholders — the renderer never reads them. */
function toBoardGoal(session: GgtSession): TacticsGoalDef {
  return {
    id: session.session_id,
    title: '',
    options: [],
    answerIndex: -1,
    funFact: '',
    players: session.goal.players,
    steps: session.goal.steps,
    bonus: { question: '', options: [], answerIndex: -1 },
  };
}

export function GuessTheGoalLive({ backHref }: { backHref?: string } = {}) {
  const queryClient = useQueryClient();
  const { addXP } = usePlayer();
  const deps = useMemo<GgtScreenDeps>(
    () => ({
      api: guessTheGoalApi,
      onRewards: (awards) => {
        if (awards.coins > 0) void queryClient.invalidateQueries({ queryKey: queryKeys.store.wallet() });
        if (awards.xp > 0) addXP(awards.xp);
      },
      track: (event, properties) => trackEvent(event, properties as Parameters<typeof trackEvent>[1]),
      isAdmin: useAuthStore.getState().user?.role === 'admin',
    }),
    [queryClient, addXP]
  );
  return <GuessTheGoalScreen backHref={backHref} deps={deps} />;
}

export function GuessTheGoalScreen({ backHref, deps }: { backHref?: string; deps: GgtScreenDeps }) {
  const t = useMiniT();
  const locale = useMiniLocale();
  const { api, partner, guest } = deps;
  /** Each goal is one public-page session: a goal after the first opens a new one ('replay'), a resumed goal too. */
  const guestGoalsRef = useRef(0);
  const emitGuestStart = useCallback(() => {
    if (!guest?.onEvent) return;
    if (guestGoalsRef.current > 0) guest.onEvent('replay');
    guest.onEvent('start');
    guestGoalsRef.current += 1;
  }, [guest]);
  const trackEvent = deps.track ?? noTrack;

  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<GgtLiveSession | null>(null);
  const [outcome, setOutcome] = useState<GgtLiveGuessOutcome | null>(null);
  const [bonusOutcome, setBonusOutcome] = useState<GgtLiveBonusOutcome | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [bonusPicked, setBonusPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [gallery, setGallery] = useState<GgtGallery | null>(null);

  /** Whole published pool solved — replays pay nothing, so the idle card must
   *  stop promising coins. Falls back to counts when the backend predates the
   *  pool_exhausted field. */
  const poolExhausted = gallery
    ? (gallery.pool_exhausted ?? (gallery.solved > 0 && gallery.solved >= gallery.total))
    : false;
  /** Five goals a day. Optional-chained so an older backend (no limit fields)
   *  simply behaves as before rather than locking the mode out. */
  const dailyLimit = gallery?.daily_goal_limit ?? null;
  const goalsToday = gallery?.goals_today ?? 0;
  const dailyLimitReached = gallery?.daily_limit_reached ?? false;
  const isAdmin = deps.isAdmin ?? false;
  /** Admin content browser: the whole pool rendered through the normal
   *  collection panel, every card unlocked. */
  const [devAll, setDevAll] = useState<GgtGallery | null>(null);
  const devToggleAllGoals = useCallback(async () => {
    if (devAll) {
      setDevAll(null);
      return;
    }
    try {
      if (!api.devAllGoals) return;
      const { goals } = await api.devAllGoals();
      setDevAll({
        solved: goals.length,
        total: goals.length,
        coins_earned: 0,
        xp_earned: 0,
        daily_coin_cap: 0,
        coins_today: 0,
        goals: goals.map((g) => ({
          title: g.title,
          year: g.year,
          difficulty: g.difficulty,
          points: g.featured_rank ?? 0,
          bonus_correct: null,
          video_url: g.video_url,
          solved_at: '',
          players: g.players,
          steps: g.steps,
        })),
        locked: {},
      });
    } catch {
      setError(t('Something went wrong — try again'));
    }
  }, [api, devAll, t]);

  const goalsLeftToday = dailyLimit === null ? null : Math.max(0, dailyLimit - goalsToday);
  const [showGallery, setShowGallery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [time, setTime] = useState(0);
  /** serverNow - clientNow at payload receipt; keeps our clock honest. */
  const offsetRef = useRef(0);
  const nonceRef = useRef<string | null>(null);

  // Goal copy is served in every app locale; mini-game chrome stays en/ka.
  const { locale: textLocale } = useLocale();
  const pick = useCallback(
    (text: GgtI18nText | null | undefined): string => {
      if (!text) return '';
      return (textLocale === 'en' ? null : text[textLocale]) ?? text.en;
    },
    [textLocale]
  );

  const boardGoal = useMemo(() => (session ? toBoardGoal(session) : null), [session]);
  const timeline = useMemo(() => (boardGoal ? buildTimeline(boardGoal) : null), [boardGoal]);

  const adoptSession = useCallback((next: GgtLiveSession, resumed = false, offsetMs?: number) => {
    // A session resumed from memory carries an old server_now: keep the offset measured when it arrived.
    offsetRef.current = offsetMs ?? new Date(next.server_now).getTime() - Date.now();
    setTime(0);
    setSession(next);
    setOutcome(next.outcome ?? null);
    setBonusOutcome(null);
    setPicked(next.guess_option_id ?? null);
    setBonusPicked(null);
    setShowVideo(false);
    setError(null);
    // 'guessed' without a bonus payload should not happen (the server only
    // parks in 'guessed' when a bonus exists) — degrade to the done panel so
    // the user always has a Next Goal button.
    setPhase(next.state === 'guessed' ? (next.bonus ? 'bonus' : 'bonus_done') : 'watch');
    trackEvent('ggt_session_started', {
      resumed,
      repeat_view: next.max_points < 100,
      difficulty: next.goal.difficulty,
      main_moves: next.goal.main_moves,
      duration_seconds: next.goal.duration_seconds,
      solved: next.progress.solved,
      total_goals: next.progress.total,
    });
  }, [trackEvent]);

  const loadCurrent = useCallback(
    (track = true) => {
      api
        .current()
        .then((existing) => {
          if (existing) {
            // Landing always shows the menu + collection (owner decision);
            // the open goal waits behind the button instead of swallowing
            // the navigation. Kick off resumes it — nothing is abandoned.
            resumableRef.current = existing;
            resumableOffsetRef.current = new Date(existing.server_now).getTime() - Date.now();
            setPhase('idle');
            if (track) trackEvent('ggt_screen_viewed', { entry: 'resumable' });
          } else {
            setPhase('idle');
            if (track) trackEvent('ggt_screen_viewed', { entry: 'idle' });
          }
        })
        .catch((err) => {
          if (apiError(err)?.status === 503) {
            setPhase('disabled');
            if (track) trackEvent('ggt_screen_viewed', { entry: 'disabled' });
          } else {
            setPhase('load_error');
            trackEvent('ggt_error', { stage: 'load' });
          }
          void err;
        });
    },
    [api, trackEvent]
  );

  /** Freecroco: after a refused or unanswered request the play itself decides the screen — its result if it ended
   *  (the start screen would only answer "no plays left"), the open goal otherwise. False when it could not tell. */
  const reconcilePartner = useCallback(
    async (sessionId: string, gen: number): Promise<boolean> => {
      if (!partner) return false;
      try {
        const view = await partner.lookup(sessionId);
        if (gen !== genRef.current) return true;
        if (view.session) {
          adoptSession(view.session, true);
          return true;
        }
        if (!view.finished) return false;
        if (!view.finished.sent || !view.outcome) {
          partner.onExit();
          return true;
        }
        setOutcome(view.bonus ? withFootage(view.outcome, view.bonus) : view.outcome);
        setBonusOutcome(view.bonus);
        setError(null);
        setPhase(view.bonus ? 'bonus_done' : 'reveal');
        return true;
      } catch {
        return false;
      }
    },
    [partner, adoptSession]
  );

  const retryLoad = useCallback(() => {
    setPhase('loading');
    setError(null);
    loadCurrent();
  }, [loadCurrent]);

  const devResetToday = useCallback(async () => {
    try {
      await api.devResetToday?.();
      genRef.current += 1;
      resumableRef.current = null;
      setSession(null);
      setOutcome(null);
      setBonusOutcome(null);
      setPicked(null);
      setError(null);
      // Through 'loading' so the menu is guaranteed to re-render (a ref
      // change alone leaves a stale 'Continue' label on screen).
      setPhase('loading');
      loadCurrent(false);
      api.gallery?.().then(setGallery).catch(() => {});
    } catch {
      setError(t('Something went wrong — try again'));
    }
  }, [api, t, loadCurrent]);

  const loadedOnceRef = useRef(false);
  useEffect(() => {
    // A failed resume must NOT fall through to 'idle': Kick off from there
    // would abandon a session (and its pending bonus) the user still has.
    // Ref-guarded: Strict Mode's double-mount must not double-fire the
    // request or the screen-view/session-start analytics.
    if (loadedOnceRef.current) return;
    loadedOnceRef.current = true;
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    // Progress data feeds the home screen and the collection overlay; reveal
    // and bonus_done refresh it so the numbers are current after every solve,
    // and opening the overlay always refetches so it can never show stale
    // totals from before the last solve.
    if (!api.gallery) return;
    if (!showGallery && phase !== 'idle' && phase !== 'reveal' && phase !== 'bonus_done') return;
    let cancelled = false;
    api
      .gallery()
      .then((data) => {
        if (!cancelled) setGallery(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, phase, showGallery]);

  useEffect(() => {
    if (!showGallery) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowGallery(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showGallery]);

  /** Server-clock elapsed seconds past the grace window. */
  const serverElapsed = useCallback((): number => {
    if (!session) return 0;
    const now = Date.now() + offsetRef.current + SKEW_PAD_MS;
    return Math.max(0, (now - new Date(session.started_at).getTime() - session.grace_ms) / 1000);
  }, [session]);

  useEffect(() => {
    // The board freezes the instant an answer is locked in — the reveal takes
    // over whenever the server responds.
    if (phase !== 'watch' || !timeline || picked !== null) return undefined;
    let raf = 0;
    const tick = () => {
      setTime(serverElapsed());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, timeline, serverElapsed, picked]);

  const start = useCallback(async () => {
    if (busy) return;
    if (resumableRef.current) {
      const resume = resumableRef.current;
      resumableRef.current = null;
      adoptSession(resume, true, resumableOffsetRef.current);
      emitGuestStart();
      return;
    }
    setBusy(true);
    setError(null);
    // Optimistic: swap to the board skeleton NOW — the session request rides
    // inside the transition instead of blocking the tap.
    setPhase('starting');
    const gen = genRef.current;
    // The nonce survives retries so a lost response can't mint two sessions.
    if (!nonceRef.current) nonceRef.current = newNonce();
    try {
      const next = await api.start(nonceRef.current);
      nonceRef.current = null;
      if (gen !== genRef.current) return;
      adoptSession(next);
      emitGuestStart();
    } catch (err) {
      if (gen !== genRef.current) return;
      const failure = apiError(err);
      if (failure) {
        if (failure.status === 503) {
          setPhase('disabled');
          return;
        }
        // Only a definite 4xx invalidates the nonce; a 5xx/timeout may have
        // committed, and retrying with a FRESH nonce would abandon that
        // committed session (free-kicks precedent).
        if (failure.status >= 400 && failure.status < 500) {
          nonceRef.current = null;
          if (failure.status === 409) {
            if (partner && failure.code === 'quota_exhausted') {
              setPhase('idle');
              setError(partner.copy.noPlaysLeft);
              return;
            }
            if (failure.code === 'GGT_DAILY_LIMIT_REACHED') {
              // The idle card disables PLAY, so reaching here means its
              // gallery data was stale — refresh it so the card catches up.
              setPhase('idle');
              setError(t('All {limit} goals played — come back tomorrow', {
                limit: gallery?.daily_goal_limit ?? 5,
              }));
              api.gallery?.().then(setGallery).catch(() => {});
              return;
            }
            const existing = await api.current().catch(() => null);
            if (gen !== genRef.current) return;
            if (existing) {
              adoptSession(existing, true);
              return;
            }
          }
        }
      }
      setPhase('idle');
      setError(t('Something went wrong — try again'));
      trackEvent('ggt_error', { stage: 'start' });
    } finally {
      setBusy(false);
    }
  }, [api, partner, trackEvent, busy, adoptSession, emitGuestStart, t, gallery?.daily_goal_limit]);

  const submitGuess = useCallback(
    async (optionId: string) => {
      if (!session || busy || phase !== 'watch') return;
      setBusy(true);
      setError(null);
      setPicked(optionId);
      // Sampled before the request so network latency doesn't inflate it.
      const watchSeconds = Math.round(serverElapsed());
      const gen = genRef.current;
      try {
        // Same-option retries replay the stored result server-side, so a
        // timeout here is safe to re-submit.
        const result = await api.guess(session.session_id, optionId);
        if (gen !== genRef.current) return;
        setOutcome(result);
        setPhase('reveal');
        deps.onRewards?.(result.awards);
        if (result.session_state === 'complete') guest?.onEvent?.('complete');
        trackEvent('ggt_guess_submitted', {
          correct: result.correct,
          points: result.points,
          revealed_moves: result.revealed_moves,
          main_moves: session.goal.main_moves,
          watch_seconds: watchSeconds,
          difficulty: session.goal.difficulty,
          first_solve: result.awards.first_solve,
          coins_awarded: result.awards.coins,
          xp_awarded: result.awards.xp,
          daily_cap_reached: result.awards.daily_cap_reached,
          has_bonus: Boolean(result.bonus),
        });
      } catch (err) {
        if (gen !== genRef.current) return;
        const failure = apiError(err);
        if (failure && failure.status >= 400 && failure.status < 500) {
          // Definite rejection (session gone/answered in another tab): a
          // same-option retry can never succeed — reconcile with the server.
          if (await reconcilePartner(session.session_id, gen)) return;
          setPhase('loading');
          loadCurrent(false);
          return;
        }
        // picked is KEPT: an ambiguous failure (timeout/5xx) may have
        // committed, and a same-option retry replays the stored result.
        setError(t('Something went wrong — try again'));
        trackEvent('ggt_error', { stage: 'guess' });
        void err;
      } finally {
        setBusy(false);
      }
    },
    [api, deps, guest, trackEvent, session, busy, phase, t, serverElapsed, loadCurrent, reconcilePartner]
  );

  const submitBonus = useCallback(
    async (optionId: string) => {
      if (!session || busy) return;
      setBusy(true);
      setError(null);
      setBonusPicked(optionId);
      const gen = genRef.current;
      try {
        const result = await api.bonus(session.session_id, optionId);
        if (gen !== genRef.current) return;
        setBonusOutcome(result);
        setOutcome((current) => withFootage(current, result));
        setPhase('bonus_done');
        deps.onRewards?.(result.awards);
        guest?.onEvent?.('complete');
        trackEvent('ggt_bonus_answered', {
          correct: result.correct,
          bonus_points: result.bonus_points,
          coins_awarded: result.awards.coins,
          xp_awarded: result.awards.xp,
          difficulty: session.goal.difficulty,
        });
      } catch (err) {
        if (gen !== genRef.current) return;
        const failure = apiError(err);
        if (failure && failure.status >= 400 && failure.status < 500) {
          if (await reconcilePartner(session.session_id, gen)) return;
          setPhase('loading');
          loadCurrent(false);
          return;
        }
        setError(t('Something went wrong — try again'));
        trackEvent('ggt_error', { stage: 'bonus' });
        void err;
      } finally {
        setBusy(false);
      }
    },
    [api, deps, guest, trackEvent, session, busy, t, loadCurrent, reconcilePartner]
  );

  const mainMoves = session?.goal.main_moves ?? 1;
  const duration = timeline?.duration ?? 0;

  /** Display-only replay position ("Move 2/5") — scoring no longer derives
   *  from it, so a fast guess on a short goal isn't punished for the author's
   *  step count. */
  const revealedMoves = useMemo(() => {
    if (!timeline) return 1;
    const elapsed = Math.min(time, timeline.duration);
    let revealed = 0;
    for (const step of timeline.steps) {
      if (step.main && step.start <= elapsed) revealed += 1;
    }
    return Math.max(1, Math.min(revealed, mainMoves));
  }, [timeline, time, mainMoves]);

  /** No time decay (owner decision): a correct guess pays the session's full
   *  max_points, which is already reduced for replayed goals. */
  const potential = session?.max_points ?? 0;

  /** Post-reveal board replay: a local clock, separate from the score clock,
   *  so the finished board can be re-animated on demand. */
  const [boardReplaying, setBoardReplaying] = useState(false);
  const [replayT, setReplayT] = useState(0);
  useEffect(() => {
    if (!boardReplaying || duration <= 0) return undefined;
    const startedAt = performance.now();
    let raf = 0;
    const tick = () => {
      const elapsed = (performance.now() - startedAt) / 1000;
      if (elapsed >= duration) {
        setBoardReplaying(false);
        return;
      }
      setReplayT(elapsed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [boardReplaying, duration]);
  useEffect(() => {
    setBoardReplaying(false);
  }, [session?.session_id, showVideo]);

  /** Animation position: loop the replay while watching; freeze at full when
   *  revealed. The score clock (time) is monotonic — only the drawing loops. */
  const animTime =
    phase === 'watch' && duration > 0
      ? Math.min(time % (duration + LOOP_HOLD), duration)
      : boardReplaying
        ? Math.min(replayT, duration)
        : duration;
  const goalFlash = boardReplaying
    ? duration > 0 && animTime >= duration - 0.05
    : phase !== 'watch' || (duration > 0 && animTime >= duration - 0.05);

  // Clips we host ourselves play in a normal <video>; anything still pointing
  // at YouTube keeps the iframe. Self-hosted is the fallback-proof path — an
  // embed can be switched off by the rights holder at any time.
  const selfHostedVideo = outcome?.video_url && /^https?:\/\/[^ ]+\.mp4(\?|$)/i.test(outcome.video_url)
    ? outcome.video_url
    : null;
  const video = !selfHostedVideo && outcome?.video_url
    ? youtubeEmbed(outcome.video_url, outcome.clip_end_s)
    : null;
  const hasVideo = Boolean(selfHostedVideo || video);

  // Owner decision: once the answer (and bonus, if any) is settled, flip to
  // the real footage automatically — "back to the board" stays one tap away.
  // Once per session: pressing back must not re-trigger the flip.
  const autoFlippedForRef = useRef<string | null>(null);
  const [videoMuted, setVideoMuted] = useState(false);
  const resumableRef = useRef<GgtLiveSession | null>(null);
  const resumableOffsetRef = useRef(0);
  /** Bumped whenever the user backs out or resets: in-flight start/guess/bonus
   *  responses from before the bump must not reopen the game. */
  const genRef = useRef(0);
  useEffect(() => {
    if (!hasVideo || !session) return;
    if (phase !== 'reveal' && phase !== 'bonus_done') return;
    if (autoFlippedForRef.current === session.session_id) return;
    autoFlippedForRef.current = session.session_id;
    // No user gesture in this async chain — Safari/iOS blocks audible
    // autoplay, which would land the flip on a paused frame. Muted plays;
    // the controls (and the flip button) restore sound.
    setVideoMuted(true);
    setShowVideo(true);
  }, [phase, hasVideo, session]);

  const guestSignIn = guest ? (
    <Link
      href={guest.signInHref}
      data-testid="ggt-guest-sign-in"
      className="flex items-center gap-2 px-1 font-poppins text-[12px] font-black uppercase text-brand-yellow underline-offset-4 hover:underline"
    >
      <CoinIcon className="size-4" /> {t('Sign in to earn coins and XP for every goal you solve')}
    </Link>
  ) : null;
  const inGame = phase === 'watch' || phase === 'reveal' || phase === 'bonus' || phase === 'bonus_done';

  // Freecroco bonus deadline: a tick drives the countdown; at 0 the server settles the play as timed out. The main
  // answer has no time limit (contract §7.4).
  const [, setClockTick] = useState(0);
  const bonusPending = outcome?.session_state === 'guessed' && Boolean(outcome.bonus) && !bonusOutcome;
  const timed = Boolean(partner) && bonusPending && (phase === 'reveal' || phase === 'bonus');
  useEffect(() => {
    if (!timed) return undefined;
    const id = window.setInterval(() => setClockTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [timed]);
  const bonusLeft =
    timed && bonusPicked === null ? secondsUntil(outcome?.bonus_deadline ?? session?.bonus_deadline, offsetRef.current) : null;
  const finishedPlay = bonusOutcome?.finished ?? outcome?.finished ?? null;
  const expiryKey = partner && session && bonusLeft === 0 ? `${session.session_id}:bonus` : null;
  useEffect(() => {
    if (!partner || !session || !expiryKey) return undefined;
    // Every retry and response checks it still belongs to this session's open bonus: an answer that won the race
    // (or a new session) cancels it, and leaving the stage clears the pending retry.
    let cancelled = false;
    let retry: number | null = null;
    const gen = genRef.current;
    const current = () => !cancelled && gen === genRef.current;
    const run = async (attempt: number): Promise<void> => {
      try {
        const result = await partner.expire(session.session_id);
        if (!current()) return;
        // The settled play comes back here: without it (no bonus outcome) "See my points" would have nothing to show.
        const bonus = result.bonus;
        if (bonus) setBonusOutcome(bonus);
        setOutcome((prev) => {
          const settled = { ...prev, ...result.outcome, finished: result.finished };
          return bonus ? withFootage(settled, bonus) : settled;
        });
        setPhase('bonus_done');
      } catch (err) {
        if (!current()) return;
        // The server's clock decides: asked a moment early (inside the 1 s grace), ask again.
        if (apiError(err)?.code === 'invalid_request' && attempt < 5) {
          retry = window.setTimeout(() => void run(attempt + 1), 1_000);
          return;
        }
        // Anything else (no answer, refused) may have settled the play: ask the play for its result.
        if ((await reconcilePartner(session.session_id, gen)) || !current()) return;
        setPhase('loading');
        loadCurrent(false);
      }
    };
    void run(0);
    return () => {
      cancelled = true;
      if (retry !== null) window.clearTimeout(retry);
    };
    // session is read through expiryKey (its id); depending on the object would restart the request on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, expiryKey, loadCurrent, reconcilePartner]);

  // Freecroco: the bonus clock runs from the right answer, so go straight to the question.
  useEffect(() => {
    if (!partner || phase !== 'reveal' || !bonusPending) return undefined;
    const id = window.setTimeout(() => setPhase((p) => (p === 'reveal' ? 'bonus' : p)), 1_500);
    return () => window.clearTimeout(id);
  }, [partner, phase, bonusPending]);

  const finishPartner = () => {
    if (!partner) return;
    // A play cancelled by a block has no points to show: back to the partner home.
    if (finishedPlay?.sent) partner.onFinished(finishedPlay);
    else partner.onExit();
  };
  const activeKind = useMemo<TacticsStepKind | null>(() => {
    if (phase !== 'watch' || !timeline) return null;
    let current: TacticsStepKind | null = null;
    for (const s of timeline.steps) {
      if (s.start <= animTime && animTime < s.end && s.kind !== 'run') current = s.kind;
    }
    if (!current) {
      for (const s of timeline.steps) if (s.start <= animTime && animTime < s.end) current = s.kind;
    }
    return current;
  }, [phase, timeline, animTime]);



  const pitchColumn = session && boardGoal && timeline && (
    <div className="flex flex-col gap-1.5 lg:flex-[3]">
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="shrink-0 font-poppins text-[11px] font-black uppercase tracking-wider text-white/45">
          {partner
            ? ''
            : t('Solved {n}/{total}', { n: session.progress.solved, total: session.progress.total })}
        </span>
        {phase === 'watch' ? (
          <span className="shrink-0 font-poppins text-[11px] font-black uppercase tracking-wider text-brand-yellow">
            {t('+{points} points', { points: potential })}
          </span>
        ) : outcome ? (
          <span
            className={`font-poppins text-[11px] font-black uppercase tracking-wider ${outcome.correct ? 'text-brand-green-bright' : 'text-brand-red'}`}
          >
            {outcome.correct
              ? t('+{points} points', { points: outcome.points })
              : partner && outcome.timed_out
                ? partner.copy.timeUp
                : t('Missed')}
          </span>
        ) : null}
      </div>

      {phase === 'watch' && (
        <div className="flex items-center gap-2 px-1">
          <span className="shrink-0 rounded-full bg-white/[0.07] px-2.5 py-1 font-poppins text-[10px] font-black uppercase tracking-wide text-white/80">
            {t('Move {n}/{total}', { n: revealedMoves, total: mainMoves })}
          </span>
          {activeKind && !goalFlash && (
            <span
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/[0.07] px-2.5 py-1 font-poppins text-[10px] font-black uppercase tracking-wide"
              style={{ color: activeKind === 'shot' ? '#FFE500' : '#ffffff' }}
            >
              <GgtActionGlyph kind={activeKind} color={activeKind === 'shot' ? '#FFE500' : '#ffffff'} />
              {GGT_ACTION_META[activeKind].label[locale]}
            </span>
          )}
        </div>
      )}

      <motion.div
        className="relative w-full overflow-hidden rounded-2xl bg-black"
        style={{ aspectRatio: `${BOARD_VIEW_W} / ${BOARD_VIEW_H}`, transformStyle: 'preserve-3d', perspective: 1200 }}
        animate={{ rotateY: showVideo ? 360 : 0 }}
        transition={{ duration: 0.55, ease: 'easeInOut' }}
      >
        {showVideo && selfHostedVideo ? (
          <video
            src={`${selfHostedVideo}${outcome?.clip_start_s ? `#t=${outcome.clip_start_s}` : ''}`}
            title={pick(outcome?.title)}
            autoPlay
            muted={videoMuted}
            controls
            playsInline
            className="absolute inset-0 h-full w-full"
          />
        ) : showVideo && video ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0${video.start ? `&start=${video.start}` : ''}${video.end ? `&end=${video.end}` : ''}`}
            title={pick(outcome?.title)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <TacticsBoard2D goal={boardGoal} timeline={timeline} t={animTime} goalFlash={goalFlash} />
        )}
      </motion.div>

      {(phase === 'reveal' || phase === 'bonus_done' || (hasVideo && phase !== 'watch')) && (
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          {hasVideo && (
            <button
              type="button"
              onClick={() => {
                setVideoMuted(false);
                setShowVideo((v) => !v);
              }}
              className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand-yellow px-5 font-poppins text-[12px] font-black uppercase tracking-wide text-black transition-all hover:brightness-105 active:translate-y-[1px]"
            >
              <Clapperboard className="size-4" />
              {showVideo ? t('Back to the board') : t('Watch the real goal')}
            </button>
          )}
          {!showVideo && (
            <button
              type="button"
              disabled={boardReplaying}
              onClick={() => {
                setReplayT(0);
                setBoardReplaying(true);
              }}
              className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white/10 px-5 font-poppins text-[12px] font-black uppercase tracking-wide text-white transition-all hover:bg-white/20 active:translate-y-[1px] disabled:opacity-50"
            >
              <RotateCcw className="size-4" />
              {t('Replay board')}
            </button>
          )}
          {(phase === 'reveal' || phase === 'bonus_done') && gallery && (
            <button
              type="button"
              onClick={() => setShowGallery(true)}
              className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand-blue px-5 font-poppins text-[12px] font-black uppercase tracking-wide text-white transition-all hover:brightness-110 active:translate-y-[1px]"
            >
              <LayoutGrid className="size-4" />
              {t('Your collection')}
            </button>
          )}
        </div>
      )}

      {/* Legend is hidden during the timed watch phase: on a 390px phone it
          pushed the four answer options below the fold and players scrolled
          while their score decayed. Glyphs are taught pre-kickoff (idle) and
          re-shown at reveal. */}
      {phase !== 'watch' && <GgtLegend />}
    </div>
  );

  return (
    <div className="min-h-screen font-fun">
      <div className="mx-auto flex min-h-[calc(100dvh-4rem)] max-w-[430px] flex-col px-4 py-6 md:px-8 md:py-8 lg:max-w-6xl">
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-3 md:gap-4">
            {(partner || guest) && (phase === 'idle' || phase === 'disabled' || phase === 'load_error') ? (
              <button
                type="button"
                aria-label={t('Back')}
                onClick={partner ? partner.onExit : guest!.onExit}
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:size-11"
              >
                <ArrowLeft className="size-5 md:size-6" />
              </button>
            ) : phase === 'idle' || phase === 'disabled' || phase === 'load_error' ? (
              <Link
                href={backHref ?? '/mini-games'}
                aria-label={t('Back')}
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:size-11"
              >
                <ArrowLeft className="size-5 md:size-6" />
              </Link>
            ) : (
              <button
                type="button"
                aria-label={t('Back')}
                onClick={() => {
                  // Mid-goal back returns to the game's own menu + collection;
                  // an unanswered goal stays resumable behind 'Continue'. A
                  // pending bonus is preserved the same way — synthesized as a
                  // 'guessed' session so resume lands on the bonus question.
                  genRef.current += 1;
                  setError(null);
                  resumableOffsetRef.current = offsetRef.current;
                  if (session && !outcome) {
                    resumableRef.current = session;
                  } else if (
                    session &&
                    outcome &&
                    outcome.session_state === 'guessed' &&
                    outcome.bonus &&
                    phase !== 'bonus_done'
                  ) {
                    resumableRef.current = {
                      ...session,
                      state: 'guessed',
                      outcome,
                      bonus: outcome.bonus,
                      guess_option_id: picked ?? undefined,
                    };
                  }
                  setSession(null);
                  setOutcome(null);
                  setPhase('idle');
                  api.gallery?.().then(setGallery).catch(() => {});
                }}
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:size-11"
              >
                <ArrowLeft className="size-5 md:size-6" />
              </button>
            )}
            <h1 className="font-poppins text-[20px] uppercase leading-[1.1] text-white md:text-[32px]">
              {t('Guess the Goal')}
            </h1>
          </div>
          <p className="mt-1.5 pl-12 text-[11px] font-black uppercase tracking-[0.04em] text-white/55 md:pl-[60px] md:text-sm md:tracking-[0.08em]">
            {partner
              ? partner.copy.subtitle
              : guest
                ? t('Name the legendary goal from the coaching board')
                : t('Name the iconic goal — real rewards, server-scored')}
          </p>
        </div>
      {phase === 'loading' && (
        <div className="flex flex-1 items-center justify-center font-poppins text-sm font-bold uppercase text-white/40">
          {t('Loading…')}
        </div>
      )}

      {phase === 'load_error' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <WifiOff className="size-10 text-white/30" />
          <p className="max-w-xs font-poppins text-sm font-bold uppercase leading-relaxed text-white/55">
            {t("Couldn't load your game")}
          </p>
          <button
            type="button"
            onClick={retryLoad}
            className="flex h-12 w-full max-w-[220px] items-center justify-center rounded-2xl bg-brand-green-bright font-poppins text-sm font-black uppercase tracking-wide text-black"
          >
            {t('Try again')}
          </button>
        </div>
      )}

      {phase === 'disabled' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <WifiOff className="size-10 text-white/30" />
          <p className="max-w-xs font-poppins text-sm font-bold uppercase leading-relaxed text-white/55">
            {t('Guess the Goal is warming up — check back soon')}
          </p>
          <button
            type="button"
            onClick={retryLoad}
            className="flex h-12 w-full max-w-[220px] items-center justify-center rounded-2xl bg-white/10 font-poppins text-sm font-black uppercase tracking-wide text-white transition-colors hover:bg-white/20"
          >
            {t('Try again')}
          </button>
        </div>
      )}

      {phase === 'idle' && (
        <div className="flex flex-1 flex-col items-center gap-8 pb-8 pt-2 md:pt-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex w-full max-w-md flex-col items-center gap-4 rounded-[20px] p-8 text-center text-black md:p-10"
            style={{ backgroundColor: '#FF9600' }}
          >
            <h2 className="font-poppins text-2xl uppercase leading-[1.05] md:text-3xl">
              {t('Guess the Goal')}
            </h2>
            <p className="max-w-xs text-sm font-semibold leading-relaxed text-black/80">
              {partner
                ? partner.copy.intro
                : guest
                ? t('A legendary goal replays on the coaching board. Watch the moves and name the goal. Sign in to earn coins and XP.')
                : poolExhausted
                ? t(
                    'You have solved every goal in the library. Replays keep your mind sharp — no repeat rewards, all glory.'
                  )
                : gallery
                  ? t(
                      'A legendary goal replays on the coaching board. Name it — the first solve of each goal pays coins and XP.'
                    )
                  : t(
                      'A legendary goal replays on the coaching board. Watch the moves and name the goal.'
                    )}
            </p>
            {gallery && !poolExhausted && goalsLeftToday !== null && (
              <div
                className="rounded-full px-4 py-1.5 text-[11px] font-black uppercase tracking-wider text-black"
                style={{ backgroundColor: 'rgba(0,0,0,0.14)' }}
              >
                {dailyLimitReached
                  ? t('All {limit} goals played — come back tomorrow', { limit: dailyLimit ?? 0 })
                  : t('{left} of {limit} goals left today', {
                      left: goalsLeftToday,
                      limit: dailyLimit ?? 0,
                    })}
              </div>
            )}
            {error && <p className="max-w-xs text-xs font-bold text-black">{error}</p>}
            <button
              type="button"
              onClick={start}
              disabled={busy || (dailyLimitReached && !resumableRef.current)}
              data-testid="ggt-kickoff"
              className="font-poppins mt-2 inline-flex h-[50px] min-w-[200px] items-center justify-center gap-2 rounded-[20px] bg-black px-8 text-[20px] uppercase tracking-wide text-white transition-all hover:brightness-110 active:translate-y-[2px] disabled:opacity-60"
            >
              <Play className="size-5 fill-current" />{' '}
              {resumableRef.current
                ? t('Continue')
                : dailyLimitReached
                  ? t('Back tomorrow')
                  : t('Kick off')}
            </button>
            {isAdmin && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={devResetToday}
                  className="font-poppins rounded-full bg-black/20 px-4 py-1.5 text-[10px] font-black uppercase tracking-wider text-black"
                >
                  DEV: reset today
                </button>
                <button
                  type="button"
                  onClick={devToggleAllGoals}
                  className="font-poppins rounded-full bg-black/20 px-4 py-1.5 text-[10px] font-black uppercase tracking-wider text-black"
                >
                  {devAll ? 'DEV: hide all goals' : 'DEV: all goals'}
                </button>
              </div>
            )}
            <GgtLegend />
          </motion.div>
          {devAll && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-3xl">
              <p className="mb-2 px-1 font-poppins text-[12px] font-black uppercase tracking-wider text-brand-yellow">
                DEV: all {devAll.total} published goals
              </p>
              <GgtGalleryPanel gallery={devAll} locale={locale} t={t} pick={pick} />
            </motion.div>
          )}
          {gallery && !devAll && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="w-full max-w-3xl"
            >
              <GgtGalleryPanel gallery={gallery} locale={locale} t={t} pick={pick} earnings={!guest} />
            </motion.div>
          )}
        </div>
      )}

      {phase === 'starting' && (
        <div
          role="status"
          aria-busy="true"
          aria-label={t('Loading…')}
          className="flex min-h-0 flex-1 flex-col gap-3 pt-1 lg:my-auto lg:flex-none lg:flex-row lg:items-start lg:justify-center lg:gap-6"
        >
          <div className="flex flex-col gap-1.5 lg:flex-[3]">
            <div className="h-[26px]" />
            <div
              className="w-full animate-pulse rounded-2xl bg-[#4a9c53]/40"
              style={{ aspectRatio: `${BOARD_VIEW_W} / ${BOARD_VIEW_H}` }}
            />
            <GgtLegend />
          </div>
          <div className="flex flex-col gap-2 lg:flex-[2]">
            <div className="h-[22px]" />
            <div className="flex flex-col gap-2.5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="min-h-[52px] animate-pulse rounded-[16px] border-2 border-white/10 bg-white/[0.04] sm:min-h-[56px]"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {inGame && session && (
        <div className="flex min-h-0 flex-1 flex-col gap-3 pt-1 lg:my-auto lg:flex-none lg:flex-row lg:items-start lg:justify-center lg:gap-6">
          {pitchColumn}

          <div className="flex flex-col gap-2 lg:flex-[2]">
            <AnimatePresence mode="wait">
              {(phase === 'watch' || phase === 'reveal') && (
                <motion.div
                  key={`options-${session.session_id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex flex-col gap-1.5"
                >
                  <p className="flex items-baseline px-1 font-poppins text-[11px] font-black uppercase tracking-wider text-white/45">
                    {t('Which goal is this?')}
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {session.goal.options.map((option) => {
                      const isAnswer = outcome?.correct_option_id === option.id;
                      const isPicked = picked === option.id;
                      const state: GgtOptionState =
                        phase === 'reveal'
                          ? isAnswer
                            ? 'correct'
                            : isPicked
                              ? 'wrong'
                              : 'dim'
                          : picked !== null
                            ? isPicked
                              ? 'locked'
                              : 'dim'
                            : 'idle';
                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={phase !== 'watch' || busy || (picked !== null && picked !== option.id)}
                          onClick={() => submitGuess(option.id)}
                          data-testid="ggt-option"
                          className={GGT_OPTION_CLASS}
                          style={ggtOptionStyle(state)}
                        >
                          {pick(option.text)}
                        </button>
                      );
                    })}
                  </div>
                  {error && phase === 'watch' && picked && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitGuess(picked)}
                      className="text-xs font-semibold text-brand-red underline"
                    >
                      {error}
                    </button>
                  )}
                  {phase === 'reveal' && outcome && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 flex flex-col gap-2">
                      <p className="px-1 font-poppins text-sm font-black uppercase leading-snug text-white">
                        {pick(outcome.title)}
                      </p>
                      {/* Held back while a bonus is pending — fun facts routinely
                          name the exact person/number the bonus asks about. */}
                      {outcome.fun_fact && !(outcome.session_state === 'guessed' && outcome.bonus) && (
                        <p className="rounded-xl bg-white/[0.05] px-3.5 py-2.5 text-xs font-semibold leading-relaxed text-white/65">
                          {pick(outcome.fun_fact)}
                        </p>
                      )}
                      {outcome.awards.first_solve && (
                        <p className="flex items-center gap-2 px-1 font-poppins text-[12px] font-black uppercase text-brand-green-bright">
                          <CoinIcon className="size-4" /> +{outcome.awards.coins} · +{outcome.awards.xp} XP
                          {outcome.awards.daily_cap_reached && (
                            <span className="text-white/40">{t('(daily cap)')}</span>
                          )}
                        </p>
                      )}
                      {!partner && !guest && outcome.correct && !outcome.awards.first_solve && (
                        <p className="px-1 font-poppins text-[11px] font-bold uppercase text-white/40">
                          {t('Already solved before — no repeat rewards')}
                        </p>
                      )}
                      {guestSignIn}
                      {error && (
                        <p className="px-1 text-xs font-semibold text-brand-red">{error}</p>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          outcome.session_state === 'guessed' && outcome.bonus
                            ? setPhase('bonus')
                            : partner
                              ? finishPartner()
                              : start()
                        }
                        data-testid="ggt-reveal-next"
                        className="flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-brand-green-bright font-poppins text-sm font-black uppercase tracking-wide text-black disabled:opacity-60"
                      >
                        {outcome.session_state === 'guessed'
                          ? t('Bonus question')
                          : partner
                            ? partner.copy.seeResult
                            : t('Next goal')}
                        <ChevronRight className="size-4" />
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {(phase === 'bonus' || phase === 'bonus_done') && (
                <motion.div
                  key={`bonus-${session.session_id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex flex-col gap-1.5"
                >
                  <p className="flex items-baseline px-1 font-poppins text-[11px] font-black uppercase tracking-wider text-brand-yellow">
                    {t('Bonus question')}
                    {bonusLeft !== null && partner && (
                      <span className="ml-2 text-white/70" data-testid="ggt-bonus-timer">· {partner.copy.timeLeft(bonusLeft)}</span>
                    )}
                  </p>
                  <p className="px-1 pb-1 font-poppins text-sm font-black uppercase leading-snug text-white">
                    {pick((outcome?.bonus ?? session.bonus)?.question)}
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {((outcome?.bonus ?? session.bonus)?.options ?? []).map((option) => {
                      const isAnswer = bonusOutcome?.correct_option_id === option.id;
                      const isPicked = bonusPicked === option.id;
                      const state: GgtOptionState =
                        phase === 'bonus_done'
                          ? isAnswer
                            ? 'correct'
                            : isPicked
                              ? 'wrong'
                              : 'dim'
                          : bonusPicked !== null
                            ? isPicked
                              ? 'locked'
                              : 'dim'
                            : 'idle';
                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={phase !== 'bonus' || busy || (bonusPicked !== null && bonusPicked !== option.id)}
                          onClick={() => submitBonus(option.id)}
                          data-testid="ggt-bonus-option"
                          className={GGT_OPTION_CLASS}
                          style={ggtOptionStyle(state)}
                        >
                          {pick(option.text)}
                        </button>
                      );
                    })}
                  </div>
                  {phase === 'bonus_done' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 flex flex-col gap-2">
                      {outcome?.fun_fact && (
                        <p className="rounded-xl bg-white/[0.05] px-3.5 py-2.5 text-xs font-semibold leading-relaxed text-white/65">
                          {pick(outcome.fun_fact)}
                        </p>
                      )}
                      {guestSignIn}
                      {bonusOutcome && (bonusOutcome.awards.coins > 0 || bonusOutcome.awards.xp > 0) && (
                        <p className="flex items-center gap-2 px-1 font-poppins text-[12px] font-black uppercase text-brand-green-bright">
                          <CoinIcon className="size-4" /> +{bonusOutcome.awards.coins} · +{bonusOutcome.awards.xp} XP
                          {bonusOutcome.awards.daily_cap_reached && (
                            <span className="text-white/40">{t('(daily cap)')}</span>
                          )}
                        </p>
                      )}
                      {error && (
                        <p className="px-1 text-xs font-semibold text-brand-red">{error}</p>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={partner ? finishPartner : start}
                        data-testid="ggt-bonus-next"
                        className="flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-brand-green-bright font-poppins text-sm font-black uppercase tracking-wide text-black disabled:opacity-60"
                      >
                        {partner ? partner.copy.seeResult : t('Next goal')}
                        {partner ? <ChevronRight className="size-4" /> : <RotateCcw className="size-4" />}
                      </button>
                    </motion.div>
                  )}
                  {error && phase === 'bonus' && bonusPicked && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitBonus(bonusPicked)}
                      className="text-xs font-semibold text-brand-red underline"
                    >
                      {error}
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
      </div>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {showGallery && gallery && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                role="dialog"
                aria-modal="true"
                aria-label={t('Your collection')}
                className="fixed inset-0 z-[100] overflow-y-auto bg-[#0d1220]/95 backdrop-blur-md"
                onClick={() => setShowGallery(false)}
              >
                <div
                  className="mx-auto max-w-3xl px-4 pb-32 pt-6 md:py-12"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GgtGalleryPanel
                    gallery={gallery}
                    locale={locale}
                    t={t}
                    pick={pick}
                    onClose={() => setShowGallery(false)}
                    earnings={!guest}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
}
